import { useFormContext, useWatch } from 'react-hook-form';
import { ReactNode } from 'react';
import { lt } from '@mews/framework';
import { ContentSection, createOption } from '@mews-commander/ui';
import {
    BookableServiceData,
    ProductChargingMode,
    ProductConsumptionMoment,
    ProductPostingMode,
    ProductPricing,
} from '@mews-commander/data-access';
import { Stack, StackItem } from '@optimus-web/core';
import { EnumSelectField, SelectField } from '@mews-commander/form';
import { ServiceTimeClass } from '@mews/enums';
import { useLocalizeChargingMode } from '../../../components/localization/LocalizedChargingMode';
import { ProductFormValues } from '../../schemas/productFormSchema';
import { WithDisabledPricingFields } from './types';

interface PostingSectionProps {
    bookableServiceData: BookableServiceData;
    serviceTimeClass: ServiceTimeClass;
    description?: ReactNode;
}

const useFrequencyOptions = (bookableServiceData: BookableServiceData) => {
    const localizeChargingMode = useLocalizeChargingMode(bookableServiceData);

    return {
        absolute: [
            createOption({
                value: ProductChargingMode.Once,
                label: localizeChargingMode(ProductChargingMode.Once),
            }),
            createOption({
                value: ProductChargingMode.PerTimeUnit,
                label: localizeChargingMode(ProductChargingMode.PerTimeUnit),
            }),
            createOption({
                value: ProductChargingMode.PerPersonPerTimeUnit,
                label: localizeChargingMode(ProductChargingMode.PerPersonPerTimeUnit),
            }),
            createOption({
                value: ProductChargingMode.PerPerson,
                label: localizeChargingMode(ProductChargingMode.PerPerson),
            }),
        ],
        relative: [
            createOption({
                value: ProductPostingMode.Once,
                label: localizeChargingMode(ProductPostingMode.Once),
            }),
            createOption({
                value: ProductPostingMode.PerTimeUnit,
                label: localizeChargingMode(ProductPostingMode.PerTimeUnit),
            }),
        ],
    };
};

const useTimingEnabledValues = (serviceTimeClass: ServiceTimeClass) => {
    const pricingCharging = useWatch({ name: 'pricing.charging' });

    if (
        pricingCharging === ProductChargingMode.Once ||
        pricingCharging === ProductChargingMode.PerPerson ||
        pricingCharging === ProductPostingMode.Once
    ) {
        return [ProductConsumptionMoment.ServiceOrderStart, ProductConsumptionMoment.ServiceOrderEnd];
    }

    if (
        pricingCharging === ProductChargingMode.PerTimeUnit ||
        pricingCharging === ProductChargingMode.PerPersonPerTimeUnit ||
        pricingCharging === ProductPostingMode.PerTimeUnit
    ) {
        return serviceTimeClass === ServiceTimeClass.Night
            ? [ProductConsumptionMoment.NextTimeUnit, ProductConsumptionMoment.PostingTimeUnit]
            : [ProductConsumptionMoment.PostingTimeUnit];
    }

    return [];
};

export const PostingSection = ({
    bookableServiceData,
    serviceTimeClass,
    description,
    disabled,
}: PostingSectionProps & WithDisabledPricingFields) => {
    const { setValue, watch } = useFormContext<ProductFormValues>();

    const frequencyOptions = useFrequencyOptions(bookableServiceData);
    const timingEnabledValues = useTimingEnabledValues(serviceTimeClass);

    return (
        <ContentSection title={lt('Charging')} spacing="300" contentSpacing="200" description={description}>
            <Stack spacing="100" fill>
                <StackItem grow>
                    <SelectField
                        required
                        name="pricing.charging"
                        label={lt('Frequency')}
                        disabled={disabled?.consumptionMoment}
                        options={
                            watch('pricing.type') === ProductPricing.Absolute
                                ? frequencyOptions.absolute
                                : frequencyOptions.relative
                        }
                        onChange={(_, value) => {
                            switch (value as ProductChargingMode | ProductPostingMode) {
                                case ProductChargingMode.Once:
                                case ProductChargingMode.PerPerson:
                                case ProductPostingMode.Once:
                                    setValue('consumptionMoment', ProductConsumptionMoment.ServiceOrderStart);
                                    break;
                                case ProductChargingMode.PerTimeUnit:
                                case ProductChargingMode.PerPersonPerTimeUnit:
                                case ProductPostingMode.PerTimeUnit:
                                    setValue(
                                        'consumptionMoment',
                                        serviceTimeClass === ServiceTimeClass.Night
                                            ? ProductConsumptionMoment.NextTimeUnit
                                            : ProductConsumptionMoment.PostingTimeUnit
                                    );
                                    break;
                            }
                        }}
                    />
                </StackItem>
                <StackItem minWidth="50%">
                    <EnumSelectField
                        enumClass={ProductConsumptionMoment}
                        enumPrefix="ProductConsumptionMoment"
                        enabledValues={timingEnabledValues}
                        required
                        name="consumptionMoment"
                        label={lt('Timing')}
                    />
                </StackItem>
            </Stack>
        </ContentSection>
    );
};
